/**
 * SubmitBid.jsx — Bidder submits a bid for a matched requirement.
 *
 * Fields: quoted amount, delivery plan, proposal document (PDF ≤10 MB).
 * Handles: REQUIREMENT_CLOSED, BID_ACCESS_DENIED, FILE_TOO_LARGE.
 *
 * Requirements: 23.4
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../utils/auth';
import { buildProposalMediaUrl } from '../../utils/mediaUrls';

const ERROR_MESSAGES = {
  REQUIREMENT_CLOSED: 'This requirement is no longer accepting bids.',
  REQUIREMENT_NOT_OPEN: 'This requirement is closed — the buyer has finalized bidding.',
  BID_ACCESS_DENIED: 'You are not matched to this requirement and cannot submit a bid.',
  FILE_TOO_LARGE: 'Proposal document is too large. Maximum allowed size is 10 MB.',
  INVALID_MIME_TYPE: 'Invalid file type. Please upload a PDF file.',
  PROPOSAL_DOC_REQUIRED: 'A proposal document must be provided.',
};

export default function SubmitBid() {
  const { id: requirementId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({ quoted_amount: '', delivery_plan: '' });
  const [proposalDoc, setProposalDoc] = useState(null);
  const [proposalDocName, setProposalDocName] = useState('');
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [requirement, setRequirement] = useState(null);
  const [requirementError, setRequirementError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setRequirementError('');
    apiClient
      .get(`/api/v1/requirements/${requirementId}/`)
      .then(({ data }) => setRequirement(data))
      .catch((err) => {
        const msg = err.response?.data?.error?.message;
        setRequirementError(msg || 'Could not load requirement details.');
      });
  }, [requirementId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setApiError('');
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setProposalDoc(f);
      setProposalDocName(f.name);
      setApiError('');
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.quoted_amount) errs.quoted_amount = 'Quoted amount is required.';
    else if (Number(form.quoted_amount) <= 0) errs.quoted_amount = 'Amount must be positive.';
    if (!form.delivery_plan) errs.delivery_plan = 'Delivery plan is required.';
    if (!proposalDoc) errs.proposalDoc = 'Proposal document is required.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setApiError('');

    const formData = new FormData();
    // Field name must match BidCreateSerializer: FK is ``requirement``, not requirement_id
    formData.append('requirement', requirementId);
    formData.append('quoted_amount', String(form.quoted_amount));
    formData.append('delivery_plan', form.delivery_plan);
    formData.append('proposal_doc', proposalDoc);

    try {
      await apiClient.post('/api/v1/bids/', formData);
      setSubmitted(true);
    } catch (err) {
      const errBody = err.response?.data?.error;
      const code = errBody?.code;
      const details = errBody?.details;
      if (code === 'VALIDATION_ERROR' && details) {
        const firstKey = Object.keys(details)[0];
        const firstMsg = details[firstKey]?.[0];
        setApiError(
          firstMsg ? `${firstKey}: ${firstMsg}` : 'Invalid request data. Check all fields.',
        );
      } else {
        setApiError(
          (code && ERROR_MESSAGES[code]) || errBody?.message || 'Failed to submit bid. Please try again.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, p: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Bid Submitted
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Your bid has been submitted successfully. You will be notified if you are shortlisted.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/bidder/requirements')}>
          Back to Requirements
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 560, mx: 'auto', mt: 4, p: 3 }}
      noValidate
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/bidder/requirements')}
        sx={{
          mb: 3,
          borderColor: 'rgba(99, 102, 241, 0.4)',
          color: '#818cf8',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
          }
        }}
      >
        Back to Requirements
      </Button>

      <Typography variant="h4" gutterBottom>
        Submit Bid
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {requirement?.category || `Requirement #${requirementId}`}
      </Typography>

      {requirementError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {requirementError}
        </Alert>
      )}

      {requirement?.scope_doc_s3_key && (
        <Button
          variant="outlined"
          href={buildProposalMediaUrl(requirement.scope_doc_s3_key)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mb: 2 }}
          aria-label="View anonymized requirement document"
        >
          View Anonymized Requirement PDF
        </Button>
      )}

      <TextField
        label="Quoted Amount (Rs.)"
        name="quoted_amount"
        type="number"
        value={form.quoted_amount}
        onChange={handleChange}
        error={!!errors.quoted_amount}
        helperText={errors.quoted_amount}
        fullWidth
        required
        margin="normal"
        inputProps={{ min: 1, 'aria-label': 'Quoted amount' }}
      />

      <TextField
        label="Delivery Plan"
        name="delivery_plan"
        value={form.delivery_plan}
        onChange={handleChange}
        error={!!errors.delivery_plan}
        helperText={errors.delivery_plan}
        fullWidth
        required
        multiline
        rows={4}
        margin="normal"
        inputProps={{ 'aria-label': 'Delivery plan' }}
      />

      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          component="label"
          aria-label="Upload proposal document"
          color={errors.proposalDoc ? 'error' : 'primary'}
        >
          {proposalDocName || 'Upload Proposal Document *'}
          <input
            type="file"
            hidden
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            aria-label="Proposal document file"
          />
        </Button>
        {errors.proposalDoc && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            {errors.proposalDoc}
          </Typography>
        )}
      </Box>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ mt: 3 }}
        aria-label="Submit bid"
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Bid'}
      </Button>
      {apiError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {apiError}
        </Alert>
      )}
    </Box>
  );
}
